import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import DOMPurify from 'dompurify';
import parse from 'html-react-parser';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { ContentArticle, ContentContainer } from '../components/ContentContainer';
import { ContactForm } from '../components/ContactForm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const pickLang = (obj, lang) => {
  if (!obj) return '';
  return obj[lang] || obj.fr || obj.en || obj.mg || '';
};

export const ContentPage = () => {
  const { slug } = useParams();
  const { t, i18n } = useTranslation();
  const [page, setPage] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setPage(null);
    setNotFound(false);
    // Pass the active locale so the backend can auto-translate FR → EN/MG.
    const lang = i18n.language || 'fr';
    axios.get(`${API}/pages/${slug}`, { params: { lang } })
      .then(res => setPage(res.data))
      .catch(() => setNotFound(true));
  }, [slug, i18n.language]);

  const lang = i18n.language || 'fr';
  const title = page ? pickLang(page.title, lang) : '';
  const html = page ? pickLang(page.content, lang) : '';
  const sanitized = useMemo(
    () => (html ? DOMPurify.sanitize(html, { USE_PROFILES: { html: true } }) : ''),
    [html]
  );

  // Loading + not-found states use the same narrow container so the
  // overall layout never jumps when the data resolves.
  if (notFound) {
    return (
      <div className="min-h-screen bg-page">
        <Header />
        <ContentContainer variant="prose" testId="content-page-notfound">
          <div className="text-center py-20">
            <p className="text-lg text-text_muted">{t('common.pageNotFound') || 'Page not found'}</p>
            <Link to="/" className="text-primary hover:underline mt-4 inline-block">
              ← {t('common.backHome') || 'Back home'}
            </Link>
          </div>
        </ContentContainer>
        <Footer />
      </div>
    );
  }
  if (!page) {
    return (
      <div className="min-h-screen bg-page">
        <Header />
        <ContentContainer variant="prose" testId="content-page-loading">
          <div className="text-center py-20">
            <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        </ContentContainer>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-page">
      <Header />
      <ContentArticle title={title} testId="content-page">
        {slug === 'help-contact' && (
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-4 mb-6 flex items-center justify-between flex-wrap gap-3">
            <p className="text-sm text-text m-0">
              {t('contact.jumpBanner') || 'Une question précise ? Écrivez-nous directement via le formulaire.'}
            </p>
            <a
              href="#contact-form-heading"
              className="inline-block px-4 py-2 bg-primary text-white rounded-md text-sm font-medium hover:opacity-90"
              data-testid="jump-to-contact-form"
            >
              {t('contact.jumpButton') || 'Aller au formulaire'} ↓
            </a>
          </div>
        )}
        {sanitized
          ? parse(sanitized)
          : <p className="text-text_muted">{t('common.noContent') || 'No content yet.'}</p>}
        {slug === 'help-contact' && (
          <>
            <hr className="my-10 border-t-2 border-primary/20" />
            <h2 id="contact-form-heading" className="text-3xl font-bold text-primary mb-3" data-testid="contact-form-heading">
              {t('contact.pageHeading') || 'Nous écrire directement'}
            </h2>
            <p className="text-text_muted mb-4">
              {t('contact.pageSubheading') || 'Utilisez le formulaire ci-dessous — nous vous répondrons rapidement à l\'adresse que vous indiquez.'}
            </p>
            <ContactForm />
          </>
        )}
      </ContentArticle>
      <Footer />
    </div>
  );
};
